import { useState, useRef } from "react";
import { X, Plus, Trash2, LayoutGrid, RotateCw, Move, AlertTriangle } from "lucide-react";
import { layoutTablePositions } from "../lib/calc";

const uid = () => `t-${Date.now()}-${Math.floor(Math.random() * 999)}`;
const MINI = 5; // px per metre in the mini layout canvas
const SNAP = 0.5; // metres — snap tables to nearest 0.5 m while dragging
const snap = (v) => Math.round(v / SNAP) * SNAP;

function tableOverlapIds(rows) {
  const ids = new Set();
  const eps = 0.01;
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      const a = rows[i], b = rows[j];
      const ox = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
      const oy = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
      if (ox > eps && oy > eps) {
        ids.add(a.id);
        ids.add(b.id);
      }
    }
  }
  return ids;
}

export function TableEditor({ tables, cols = 1, onClose, onSave }) {
  const [rows, setRows] = useState(() =>
    layoutTablePositions((tables || []).map((t) => ({ ...t })), cols).map((t) => ({ ...t }))
  );
  const contentRef = useRef(null);
  const dragRef = useRef(null);

  const update = (id, key, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  const move = (id, x, y) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, x, y } : r)));
  const rotate = (id) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, w: r.h, h: r.w } : r)));

  const addRow = () =>
    setRows((prev) => {
      const maxBottom = prev.reduce((m, t) => Math.max(m, (t.y || 0) + (t.h || 0)), 0);
      return [
        ...prev,
        { id: uid(), label: `TABLE ${String.fromCharCode(65 + prev.length)}`, w: 40, h: 9, x: 0, y: prev.length ? maxBottom + 2 : 0 },
      ];
    });

  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  const save = () => {
    const cleaned = rows
      .map((r) => ({
        id: r.id,
        label: (r.label || "TABLE").trim().toUpperCase(),
        w: Math.max(1, parseFloat(r.w) || 0),
        h: Math.max(1, parseFloat(r.h) || 0),
        x: Math.max(0, +(parseFloat(r.x) || 0).toFixed(2)),
        y: Math.max(0, +(parseFloat(r.y) || 0).toFixed(2)),
      }))
      .filter((r) => r.w > 0 && r.h > 0);
    onSave(cleaned);
  };

  const onDown = (e, r) => {
    const rect = contentRef.current.getBoundingClientRect();
    dragRef.current = { id: r.id, offX: (e.clientX - rect.left) / MINI - r.x, offY: (e.clientY - rect.top) / MINI - r.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.stopPropagation();
  };
  const onMoveBox = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const rect = contentRef.current.getBoundingClientRect();
    const nx = Math.max(0, snap(+((e.clientX - rect.left) / MINI - d.offX)));
    const ny = Math.max(0, snap(+((e.clientY - rect.top) / MINI - d.offY)));
    move(d.id, nx, ny);
    e.preventDefault();
  };
  const onUp = () => { dragRef.current = null; };

  const clashing = tableOverlapIds(rows.map((r) => ({ id: r.id, x: +r.x || 0, y: +r.y || 0, w: +r.w || 0, h: +r.h || 0 })));

  const bounds = rows.reduce((a, t) => ({ w: Math.max(a.w, t.x + t.w), h: Math.max(a.h, t.y + t.h) }), { w: 0, h: 0 });
  const contentW = Math.max(bounds.w * MINI + 16, 280);
  const contentH = Math.max(bounds.h * MINI + 16, 150);

  return (
    <div
      data-testid="table-editor-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[92vh] flex flex-col bg-[#16181A] border border-[#2E3033]"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2E3033] shrink-0">
          <div className="flex items-center gap-2">
            <LayoutGrid className="w-4 h-4 text-[#2560C9]" />
            <h3 className="font-heading font-bold text-white text-sm tracking-tight">EDIT YARD LAYOUT</h3>
          </div>
          <button data-testid="table-editor-close" onClick={onClose} className="text-[#6E7787] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <span className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.15em] text-[#6E7787]">
              <Move className="w-3 h-3" /> Drag tables to arrange · snaps to 0.5 m · use ↻ to rotate
            </span>
            <div
              data-testid="layout-canvas"
              className="mt-1.5 bg-[#0A0A0A] border border-[#2E3033] overflow-auto"
              style={{ maxHeight: 260 }}
            >
              <div
                ref={contentRef}
                className="relative"
                style={{
                  width: contentW,
                  height: contentH,
                  backgroundImage:
                    "linear-gradient(#1c1f22 1px,transparent 1px),linear-gradient(90deg,#1c1f22 1px,transparent 1px)",
                  backgroundSize: `${MINI * 5}px ${MINI * 5}px`,
                }}
              >
                {rows.map((r) => {
                  const clash = clashing.has(r.id);
                  return (
                  <div
                    key={r.id}
                    data-testid={`layout-box-${r.id}`}
                    data-overlap={clash ? "true" : "false"}
                    onPointerDown={(e) => onDown(e, r)}
                    onPointerMove={onMoveBox}
                    onPointerUp={onUp}
                    onPointerCancel={onUp}
                    className={`absolute flex items-center justify-center cursor-grab active:cursor-grabbing select-none ${
                      clash ? "bg-[#FF3B30]/70 border border-[#FF3B30]" : "bg-[#2560C9]/70 border border-[#2560C9]"
                    }`}
                    style={{
                      left: r.x * MINI,
                      top: r.y * MINI,
                      width: Math.max(r.w * MINI, 30),
                      height: Math.max(r.h * MINI, 18),
                      touchAction: "none",
                    }}
                    title={`${r.label} — drag to move`}
                  >
                    <span className="text-[8px] font-mono font-bold text-white truncate px-1 pointer-events-none">{r.label}</span>
                    <button
                      data-testid={`layout-rotate-${r.id}`}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); rotate(r.id); }}
                      className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-[#0A0A0A] text-white p-0.5"
                      title="Rotate 90°"
                    >
                      <RotateCw className="w-2.5 h-2.5" />
                    </button>
                  </div>
                  );
                })}
              </div>
            </div>
            {clashing.size > 0 && (
              <div data-testid="layout-overlap-warning" className="flex items-center gap-1.5 mt-1.5 text-[10px] font-mono text-[#FF3B30]">
                <AlertTriangle className="w-3 h-3" /> {clashing.size} table{clashing.size !== 1 ? "s" : ""} overlapping — spread them out.
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_64px_64px_48px_48px_32px_32px] gap-2 px-1">
              <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#6E7787]">Name</span>
              <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#6E7787]">Len (m)</span>
              <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#6E7787]">Wid (m)</span>
              <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#6E7787]">X (m)</span>
              <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#6E7787]">Y (m)</span>
              <span />
              <span />
            </div>
            {rows.map((r) => (
              <div key={r.id} data-testid={`table-row-${r.id}`} className="grid grid-cols-[1fr_64px_64px_48px_48px_32px_32px] gap-2 items-center">
                <input
                  data-testid={`table-label-${r.id}`}
                  value={r.label}
                  onChange={(e) => update(r.id, "label", e.target.value)}
                  className="bg-[#0A0A0A] border border-[#2E3033] text-white text-xs font-mono px-2 py-1.5 focus:border-[#2560C9] focus:outline-none min-w-0"
                />
                <NumInput testid={`table-w-${r.id}`} value={r.w} onChange={(v) => update(r.id, "w", v)} />
                <NumInput testid={`table-h-${r.id}`} value={r.h} onChange={(v) => update(r.id, "h", v)} />
                <NumInput testid={`table-x-${r.id}`} value={r.x} onChange={(v) => update(r.id, "x", v)} step="0.5" />
                <NumInput testid={`table-y-${r.id}`} value={r.y} onChange={(v) => update(r.id, "y", v)} step="0.5" />
                <button
                  data-testid={`table-rotate-${r.id}`}
                  onClick={() => rotate(r.id)}
                  className="text-[#6E7787] hover:text-[#2560C9] transition-colors flex justify-center"
                  title="Rotate 90° (swap length & width)"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
                <button
                  data-testid={`table-delete-${r.id}`}
                  onClick={() => removeRow(r.id)}
                  disabled={rows.length <= 1}
                  className="text-[#6E7787] hover:text-[#EF4444] transition-colors disabled:opacity-30 flex justify-center"
                  title="Remove table"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              data-testid="table-add-row"
              onClick={addRow}
              className="flex items-center gap-1 border border-dashed border-[#2E3033] hover:border-[#2560C9] text-[#A0AABF] hover:text-white text-xs px-2.5 py-2 w-full justify-center transition-colors mt-2"
            >
              <Plus className="w-3.5 h-3.5" /> Add Table
            </button>
            <p className="text-[10px] font-mono text-[#6E7787] pt-1">
              Removing a table also removes any panels placed on it.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#2E3033] shrink-0">
          <button
            data-testid="table-editor-cancel"
            onClick={onClose}
            className="border border-[#2E3033] hover:bg-[#22252A] text-[#A0AABF] text-xs px-3 py-1.5 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="table-editor-save"
            onClick={save}
            className="bg-[#2560C9] hover:bg-[#3B75DF] text-white text-xs px-4 py-1.5 transition-colors"
          >
            Save Layout
          </button>
        </div>
      </div>
    </div>
  );
}

function NumInput({ testid, value, onChange, step = "0.5" }) {
  return (
    <input
      data-testid={testid}
      type="number"
      min="0"
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#0A0A0A] border border-[#2E3033] text-white text-xs font-mono px-1.5 py-1.5 focus:border-[#2560C9] focus:outline-none w-full min-w-0"
    />
  );
}
